import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  InputGroup,
} from "react-bootstrap";
import useInvoiceTracking from "../hooks/useInvoiceTracking";
import useAddress from "../hooks/useAddress";
import useLocation from "../hooks/useLocation";
import { useAppConfig } from "../context/AppConfigContext";
import {
  formatCurrencyLocal,
  formatNumberLocal,
} from "../utils/stringManipulator";
import useJWT from "../hooks/useJWT";

import "./tracking.css";

const formatDateTime = (value) => {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const formatDate = (value) => {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
};

const formatTime = (value) => {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const formatAddress = (shipmentDetail, fallbackAddress) => {
  if (!shipmentDetail) return fallbackAddress;

  const addressParts = [
    shipmentDetail?.name,
    shipmentDetail?.mobilePhoneNumber || shipmentDetail?.phoneNumber,
    shipmentDetail?.streetName ||
      shipmentDetail?.street ||
      shipmentDetail?.address,
    shipmentDetail?.otherDetail,
    shipmentDetail?.province,
    shipmentDetail?.city,
    shipmentDetail?.region,
    shipmentDetail?.postalCode || shipmentDetail?.zipCode,
  ]
    .filter(Boolean)
    .map((part) => String(part).trim())
    .filter(Boolean);

  const combinedLines = addressParts.join(", ");

  return combinedLines || fallbackAddress;
};

const Tracking = () => {
  const {
    fetchInvoiceTracking,
    data: invoice,
    status,
    error,
  } = useInvoiceTracking();
  const { fetchSingleAddress, single: recipientAddress } = useAddress();
  const [trackingNumber, setTrackingNumber] = useState("");
  const [submittedNumber, setSubmittedNumber] = useState("");
  const [showDetails, setShowDetails] = useState(false);
  const lastFetchedRecipientUserIdRef = useRef(null);

  const { user } = useJWT();
  const { config } = useAppConfig();

  const {
    fetchLocationList,
    fetchLocationDetails,
    data: locationData,
    single: locationSingle,
  } = useLocation();
  const lastFetchedLocationIdRef = useRef(null);
  const invoiceData = invoice && typeof invoice === "object" ? invoice : {};
  const recipientAddressData =
    recipientAddress && typeof recipientAddress === "object"
      ? recipientAddress
      : {};
  const orderItems = Array.isArray(invoiceData?.details)
    ? invoiceData.details
    : [];

  const getItemPrice = (item) => {
    if (item?.productPrice) {
      return (item.productPrice.discountPrice !== null && item.productPrice.discountPrice !== undefined && item.productPrice.discountPrice > 0)
        ? item.productPrice.discountPrice
        : item.productPrice.price;
    }
    return item?.price || 0;
  };

  const calculatedSubTotal = orderItems.reduce(
    (total, item) => total + (getItemPrice(item) * item?.qty || 0),
    0
  );

  const trackingCode =
    invoiceData?.deliveryNumber ||
    submittedNumber ||
    invoiceData?.invoiceNumber;
  const statusLabel = invoiceData?.statusName || "Pending";

  // LOGIKA DIPERBAIKI: Hilangkan kondisi status === 2 untuk Finish/Delivery, cukup bergantung pada tanggal atau penanda yang valid.
  const isPaymentDone = Boolean(invoiceData?.paymentConfirmDate);
  const isOutForDelivery = Boolean(invoiceData?.deliveryDate);
  const isFinished = Boolean(invoiceData?.completeDate);

  const createdDate = formatDateTime(invoiceData?.createdDate);
  const paymentDate = formatDateTime(invoiceData?.paymentConfirmDate);
  const deliveryDate = formatDateTime(invoiceData?.deliveryDate);
  const completeDate = formatDateTime(invoiceData?.completeDate);

  const trackingStages = [
    { title: "Order Made", date: createdDate, done: true },
    { title: "Payment Received", date: paymentDate, done: isPaymentDone },
    { title: "Out for Delivery", date: deliveryDate, done: isOutForDelivery },
    { title: "Finished", date: completeDate, done: isFinished },
  ];

  // LOGIKA DIPERBAIKI: Hapus || fallback untuk tanggal agar tidak mewarisi tanggal sebelumnya
  const trackingEventsData = [
    {
      time: formatTime(invoiceData?.completeDate),
      date: formatDate(invoiceData?.completeDate),
      title: "Finished",
      description: invoiceData?.completeDate
        ? "Package has been delivered to buyer."
        : "Final delivery stage has not been reached yet.",
      done: isFinished,
    },
    {
      time: formatTime(invoiceData?.deliveryDate),
      date: formatDate(invoiceData?.deliveryDate),
      title: "Out for delivery",
      description: invoiceData?.deliveryDate
        ? "Package is out for delivery."
        : "Waiting for shipment dispatch.",
      done: isOutForDelivery,
    },
    {
      time: formatTime(invoiceData?.paymentConfirmDate),
      date: formatDate(invoiceData?.paymentConfirmDate),
      title: "Payment Received",
      description: invoiceData?.paymentConfirmDate
        ? "Payment has been confirmed."
        : "Payment confirmation is pending.",
      done: isPaymentDone,
    },
    {
      time: formatTime(invoiceData?.createdDate),
      date: formatDate(invoiceData?.createdDate),
      title: "Order Made",
      description: "Order received from customer.",
      done: true,
    },
  ];

  const orderTotals = [
    {
      label: "Order Subtotal",
      value: formatCurrencyLocal(
        calculatedSubTotal,
        config?.currency,
      ),
    },
    {
      label: "Delivery Fee",
      value: formatCurrencyLocal(invoiceData?.shippingCost, config?.currency),
    },
    {
      label: "Delivery Discount",
      value: formatCurrencyLocal(
        invoiceData?.discountTotal ?? invoiceData?.discount,
        config?.currency,
      ),
    },
  ];

  const handleSubmit = async (event) => {
    event.preventDefault();

    const code = trackingNumber.trim();
    setSubmittedNumber(code);
    setShowDetails(true);

    await fetchInvoiceTracking(code);
  };

  useEffect(() => {
    const addressId = invoiceData?.addressId;

    if (!addressId || lastFetchedRecipientUserIdRef.current === addressId) {
      return;
    }

    lastFetchedRecipientUserIdRef.current = addressId;
    fetchSingleAddress(addressId);
  }, [fetchSingleAddress, invoiceData?.addressId]);

  useEffect(() => {
    const payload = {
      skip: 0,
      take: 1,
      filter: "",
      startDate: null,
      endDate: null,
      quickFilter: [],
      sort: [],
      isActive: true,
      orderFulfillment: true,
    };

    // fetch first location entry (take:1)
    fetchLocationList(payload);
  }, [fetchLocationList]);

  useEffect(() => {
    const targetId =
      locationData?.list && locationData.list.length > 0
        ? locationData.list[0].id
        : null;
    if (!targetId) return;
    if (lastFetchedLocationIdRef.current === targetId) return;
    lastFetchedLocationIdRef.current = targetId;
    fetchLocationDetails(targetId);
  }, [locationData, fetchLocationDetails]);



  const renderEmptyState = () => (
    <Card className="border-0 rounded-24 shadow-sm overflow-hidden">
      <Card.Body className="p-24 p-md-32 text-center">
        <div className="mx-auto mb-24" style={{ maxWidth: 340 }}>
          <img
            src={`assets/images/thumbs/tracking.svg`}
            alt="Tracking illustration placeholder"
            className="img-fluid"
          />
        </div>
      </Card.Body>
    </Card>
  );

  const renderLoadingState = () => (
    <Card className="border-0 rounded-24 shadow-sm overflow-hidden">
      <Card.Body className="p-24 p-md-32 text-center">
        <div className="d-inline-flex align-items-center gap-8 mb-16 text-gray-700">
          <span
            className="spinner-border spinner-border-sm"
            role="status"
            aria-hidden="true"
          />
          <span>
            Loading tracking for {submittedNumber || trackingNumber}...
          </span>
        </div>
        <p className="mb-0 text-gray-600">
          Please wait while we fetch invoice tracking data.
        </p>
      </Card.Body>
    </Card>
  );

  const renderErrorState = () => (
    <Card className="border-0 rounded-24 shadow-sm overflow-hidden">
      <Card.Body className="p-24 p-md-32 text-center">
        <div className="mx-auto mb-24" style={{ maxWidth: 340 }}>
          <img
            src={`assets/images/thumbs/tracking.svg`}
            alt="Tracking illustration placeholder"
            className="img-fluid"
          />
        </div>
        <h5 className="mb-12 fw-semibold text-gray-900">Tracking not found</h5>
        <p className="mb-0 text-gray-600">{error || "Something went wrong"}</p>
      </Card.Body>
    </Card>
  );

  const renderDetailsState = () => (
    <div className="tracking-result animate__animated animate__fadeInUp">
      <Card className="tracking-details-card border-0 rounded-24 shadow-sm overflow-hidden mb-24 border border-gray-100">
        <Card.Body className="p-24 p-lg-32">
          <div className="text-center mb-40">
            <h4 className="mb-8 fw-bold text-gray-900">
              Invoice No: {invoiceData?.invoiceNumber || trackingCode}
            </h4>
            <div className="text-success-600 fw-medium mb-4">{statusLabel}</div>
            <div className="text-gray-500 text-sm">{createdDate}</div>
          </div>

          <div className="stepper-horizontal mb-48 px-lg-40">
            {trackingStages.map((stage, index) => (
              <div key={stage.title} className="stepper-item">
                <div className={`stepper-circle ${stage.done ? "active" : ""}`}>
                  <i className="ph ph-check" />
                </div>
                {index < trackingStages.length - 1 && (
                  <div
                    className={`stepper-line ${trackingStages[index + 1]?.done ? "active" : ""}`}
                  />
                )}
                <div className="stepper-title">{stage.title}</div>
                <div className="stepper-date">{stage.date}</div>
              </div>
            ))}
          </div>

          <Row className="gy-4">
            <Col md={6}>
              <div className="mb-32 pb-32 border-bottom border-gray-100">
                <h5 className="mb-16 fw-semibold text-gray-900">
                  Pickup Address
                </h5>
                {user ? (
                  <>
                    {locationSingle &&
                    Object.keys(locationSingle).length > 0 ? (
                      <>
                        <div className="fw-semibold text-gray-900 mb-8">
                          {locationSingle?.name ||
                            invoiceData?.pickupName ||
                            "Pink Peony Shop"}
                        </div>
                        <div className="text-sm text-gray-500 lh-lg">
                          {formatAddress(
                            locationSingle,
                            invoiceData?.pickupAddress || (
                              <>
                                Jl. Holis Regency No.40 Blok M, Babakan, Babakan
                                Ciparay, <br />
                                Bandung City, West Java 40222
                              </>
                            ),
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="fw-semibold text-gray-900 mb-8">
                          {invoiceData?.pickupName || "Pink Peony Shop"}
                        </div>
                        <div className="text-sm text-gray-500 lh-lg">
                          {invoiceData?.pickupAddress || (
                            <>
                              Jl. Holis Regency No.40 Blok M, Babakan, Babakan
                              Ciparay, <br />
                              Bandung City, West Java 40222
                            </>
                          )}
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    Please{" "}
                    <a href="/login" className="text-primary">
                      log in
                    </a>{" "}
                    to view pickup address details.
                  </>
                )}
              </div>

              <div>
                <h5 className="mb-16 fw-semibold text-gray-900">
                  Recipient Address
                </h5>
                {user ? (
                  <>
                    <div className="fw-semibold text-gray-900 mb-8">
                      {recipientAddressData?.name ||
                        recipientAddressData?.recipientName ||
                        invoiceData?.recipientName ||
                        "Iwant Asia"}
                    </div>
                    <div className="text-sm text-gray-500 lh-lg">
                      {formatAddress(
                        recipientAddressData,
                        invoiceData?.recipientAddress || (
                          <>
                            Jl. Holis Regency No.40 Blok M, Babakan, Babakan
                            Ciparay, <br />
                            Bandung City, West Java 40222
                          </>
                        ),
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    Please{" "}
                    <a href="/login" className="text-primary">
                      log in
                    </a>{" "}
                    to view recipient address details.
                  </>
                )}
              </div>
            </Col>

            <Col md={6}>
              <Card className="border border-gray-100 shadow-none rounded-16 h-100">
                <Card.Body className="p-24">
                  <h5 className="mb-16 fw-semibold text-gray-900">
                    Order Tracking History
                  </h5>
                  <p className="text-sm text-gray-900 mb-4 fw-medium">
                    Please track this order with
                  </p>
                  <span className="text-primary text-sm d-inline-block mb-32">
                    {trackingCode}
                  </span>

                  <div className="tracking-history-vertical">
                    {trackingEventsData.map((entry, index) => (
                      <div
                        key={`${entry.title}-${entry.time}`}
                        className="d-flex align-items-start gap-16 mb-20"
                      >
                        <div className="text-center" style={{ width: 80 }}>
                          <div className="fw-semibold text-gray-900 text-sm">
                            {entry.time}
                          </div>
                          <div className="text-xs text-gray-500">
                            {entry.date}
                          </div>
                        </div>
                        <div className="tracking-history-visual">
                          <div
                            className="tracking-history-circle"
                            style={{
                              backgroundColor: entry.done
                                ? "#A91E12"
                                : "#E5E7EB",
                            }}
                          >
                            {entry.done && <i className="ph ph-check" />}
                          </div>
                          {index < trackingEventsData.length - 1 && (
                            <div className="tracking-history-line" />
                          )}
                        </div>
                        <div className="flex-grow-1 mt-1">
                          <div className="fw-semibold text-gray-900 text-sm mb-4">
                            {entry.title}
                          </div>
                          <div className="text-sm text-gray-500">
                            {entry.description}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Card className="order-details-card border border-gray-100 rounded-24 shadow-sm overflow-hidden mt-24">
            <Card.Body className="p-0">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-16 p-24 border-bottom border-gray-100">
                <div className="d-flex align-items-center gap-16">
                  <div
                    className="rounded-circle border border-gray-200 overflow-hidden d-flex align-items-center justify-content-center bg-gray-50"
                    style={{ width: "56px", height: "56px" }}
                  >
                    <i className="ph ph-package fs-3 text-gray-400" />
                  </div>
                  <h4 className="mb-0 fw-bold text-gray-900">
                    Invoice Details
                  </h4>
                </div>
                <div className="d-flex align-items-center gap-12">
                  {invoiceData?.userId === user?.id && (
                    <Button
                      variant="light"
                      className="bg-transparent border border-gray-300 rounded-pill px-24 py-10 fw-medium text-gray-700"
                      onClick={() =>
                        window.open(
                          `/order-detail?id=${invoiceData?.id}`,
                          "_blank",
                        )
                      }
                    >
                      View Invoice
                    </Button>
                  )}

                  <button
                    type="button"
                    onClick={() =>
                      window.open(
                        `https://wa.me/6587333600?text=Hello%20I%20want%20to%20inquire%20about%20my%20order%20with%20tracking%20number%20${trackingCode}`,
                        "_blank",
                      )
                    }
                    style={{
                      borderColor: "#A91E12",
                      color: "#A91E12",
                      backgroundColor: "transparent",
                      borderWidth: 1,
                      borderStyle: "solid",
                      borderRadius: "50px",
                      padding: "0.4rem 1.2rem",
                      fontWeight: 500,
                    }}
                  >
                    Chat
                  </button>
                </div>
              </div>

              <div className="p-24 p-lg-32">
                <h5 className="mb-24 fw-bold text-gray-900">Order</h5>

                <div className="d-flex flex-column mb-32">
                  {orderItems.length > 0 ? (
                    orderItems.map((item, index) => {
                      const itemName =
                        item?.productName ||
                        item?.productId ||
                        `Item ${index + 1}`;
                      const itemVariant =
                        item?.variantCombinationName ||
                        item?.variantCombinationId ||
                        "-";
                      const itemImage =
                        item?.photoData?.[0]?.url || item?.photo || "";

                      return (
                        <div
                          key={`${item?.productId || index}-${item?.variantCombinationId || index}`}
                          className={`d-flex flex-wrap align-items-start gap-16 pb-24 ${index !== orderItems.length - 1 ? "mb-24 border-bottom border-gray-100" : ""}`}
                        >
                          <div className="d-flex min-w-200">
                            <div
                              className="rounded-12 border border-gray-100 d-flex align-items-center justify-content-center p-8 bg-white flex-shrink-0 align-self-start"
                              style={{ width: "80px", height: "80px" }}
                            >
                              {itemImage ? (
                                <img
                                  src={itemImage}
                                  alt={itemName}
                                  className="img-fluid object-fit-contain"
                                />
                              ) : (
                                <i className="ph ph-image fs-2 text-gray-300" />
                              )}
                            </div>
                            <div style={{ marginLeft: 15 }}>
                              <div className="d-flex align-items-center gap-4">
                                <h6 className="mb-8 fw-semibold text-gray-900 text-sm md:text-lg">
                                  {itemName}
                                </h6>
                                <div
                                  className="text-lg text-gray-900 fw-medium mb-12"
                                  style={{ minWidth: 50, textAlign: "end" }}
                                >
                                  × {formatNumberLocal(item?.qty ?? 0)}
                                </div>
                              </div>
                              <div>
                                <span className="text-sm text-gray-500 d-block">
                                  Variant: {itemVariant}
                                </span>
                                {item?.productPrice?.uom && (
                                  <span className="text-sm text-gray-500 d-block mt-1">
                                    {item.productPrice.uom} / Qty {item.productPrice.conversionQty}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="text-end ms-auto min-w-100 d-flex flex-column align-items-end">
                            {item?.productPrice && item.productPrice.discountPrice !== null && item.productPrice.discountPrice !== undefined && item.productPrice.discountPrice > 0 && (
                              <span className="text-sm text-gray-400 text-decoration-line-through mb-4">
                                {formatCurrencyLocal(item.productPrice.price, config?.currency)}
                              </span>
                            )}
                            <span className="fw-bold text-gray-900">
                              {formatCurrencyLocal(
                                getItemPrice(item),
                                config?.currency,
                              )}
                            </span>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-sm text-gray-500">
                      No order items found for this invoice.
                    </div>
                  )}
                </div>

                <hr className="border-gray-200 my-0 mb-24" />

                <div className="d-flex flex-column gap-12 mb-32">
                  {orderTotals.map((row) => (
                    <div
                      key={row.label}
                      className="d-flex justify-content-between align-items-center text-sm"
                    >
                      <span className="text-gray-600">{row.label}</span>
                      <span className="text-gray-900 fw-medium">
                        {row.value}
                      </span>
                    </div>
                  ))}
                </div>

                <hr className="border-gray-200 my-0 mb-24" />

                <div className="d-flex justify-content-between align-items-center">
                  <span className="fw-bold text-gray-900 fs-5">
                    Total Order :
                  </span>
                  <span className="fw-bold text-gray-900 fs-5">
                    {formatCurrencyLocal(
                      invoiceData?.totalPayment ?? 0,
                      config?.currency,
                    )}
                  </span>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Card.Body>
      </Card>
    </div>
  );

  return (
    <main className="tracking-page" style={{ background: "#fff" }}>
      <section className="py-32 py-lg-40">
        <Container>
          <Row className="justify-content-center">
            <Col lg={12} xl={12}>
              <Card className="search-card border-0 shadow-none rounded-24 overflow-hidden mb-24">
                <Card.Body className="p-20 p-md-24">
                  <Form onSubmit={handleSubmit}>
                    <Row className="align-items-center gx-3">
                      <Col xs={12}>
                        <InputGroup className="rounded-pill border border-gray-200 overflow-hidden bg-white">
                          <InputGroup.Text className="bg-transparent border-0 pe-0">
                            <i className="ph ph-magnifying-glass text-gray-400" />
                          </InputGroup.Text>
                          <Form.Control
                            size="lg"
                            type="text"
                            value={trackingNumber}
                            onChange={(event) =>
                              setTrackingNumber(event.target.value)
                            }
                            placeholder="Input your shipping number or invoice number"
                            className="border-0 shadow-none py-14 px-18 bg-transparent"
                          />
                        </InputGroup>
                      </Col>
                    </Row>
                    <Row className="justify-content-center mt-24">
                      <Col xs="auto">
                        <Button
                          type="submit"
                          className="btn bg-main-one-600 rounded-pill px-32 py-12 d-inline-flex align-items-center gap-8 shadow-none"
                          style={{
                            backgroundColor: "#A91E12",
                            color: "#fff",
                            border: "none",
                          }}
                        >
                          <i className="ph ph-magnifying-glass" />
                          Search
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                </Card.Body>
              </Card>

              {!showDetails
                ? renderEmptyState()
                : status === "loading"
                  ? renderLoadingState()
                  : status === "error"
                    ? renderErrorState()
                    : renderDetailsState()}
            </Col>
          </Row>
        </Container>
      </section>
    </main>
  );
};

export default Tracking;
